/* eslint-disable react/no-multi-comp */
import React, { PureComponent } from 'react';
import styled, { keyframes, css } from 'styled-components';

import { getRequest, batchRequest } from '../../../api/ApiService';
import { changeLocation } from '../../../api/Location';
import Auth from '../../../routes/Auth';
import Teams from '../../../api/Teams';

import {
  CustomerPreferences,
  ProductStrategy,
  MachineOptions,
  DistributionCenters,
  ShareBuyback,
  NewMetricROE,
  NewMetricROA,
  NewMetricEPS,
} from '../what-to-expect';

// Consolidated onboarding checklist for rounds 2+. Same visual
// design as Round1Checklist but content is different:
//
//   Step 1 — Review Prior Round Results (substeps)
//     - Review Performance Feedback report (only if didn't hit
//       every target in the prior round)
//     - Review Team Report (always)
//
//   Step 2 — What to Expect This Round (substeps)
//     Conditional on round number + active financial targets:
//       - Customer Preferences (rounds 2+)
//       - Product Strategy / Machine Options / Distribution Centers
//         / Share Buyback (round 2 only)
//       - New Metric: ROE / ROA / EPS (when each is an active
//         target for this round)
//     Each substep opens the existing What-to-Expect component
//     inside a modal — same pattern as the Team Contract popup
//     in Round1Checklist.
//
// Persistence: per-round localStorage key so progress is tracked
// independently each round. No backend endpoint yet.

// ---------- copy ---------------------------------------------------

const COMPLETION_COPY = {
  icon: '✓',
  title: "You're ready for this round.",
  description: 'Great work. Make sure to post all team decisions before the round ends.',
};

const POPUP_COMPONENTS = {
  'customer-preferences': CustomerPreferences,
  'product-strategy': ProductStrategy,
  'machine-options': MachineOptions,
  'distribution-centers': DistributionCenters,
  'share-buyback': ShareBuyback,
  'new-metric-roe': NewMetricROE,
  'new-metric-roa': NewMetricROA,
  'new-metric-eps': NewMetricEPS,
};

const POPUP_TITLES = {
  'customer-preferences': 'Customer Preferences',
  'product-strategy': 'Product Strategy',
  'machine-options': 'Machine Options',
  'distribution-centers': 'Distribution Centers',
  'share-buyback': 'Share Buyback',
  'new-metric-roe': 'New Metric: ROE',
  'new-metric-roa': 'New Metric: ROA',
  'new-metric-eps': 'New Metric: EPS',
};

const POPUP_SUBSTEPS = {
  'customer-preferences': {
    title: 'Customer Preferences',
    description: 'Review the latest customer preferences for this round.',
    linkText: 'View customer preferences',
  },
  'product-strategy': {
    title: 'Product Strategy',
    description: 'Get familiar with the product strategy changes for this round.',
    linkText: 'View product strategy',
  },
  'machine-options': {
    title: 'Machine Options',
    description: 'New machine options are available this round.',
    linkText: 'View machine options',
  },
  'distribution-centers': {
    title: 'Distribution Centers',
    description: 'New distribution centers come online this round.',
    linkText: 'View distribution centers',
  },
  'share-buyback': {
    title: 'Share Buyback',
    description: 'A share buyback option becomes available this round.',
    linkText: 'View share buyback',
  },
  'new-metric-roe': {
    title: 'New Metric: ROE',
    description: 'Return on Equity is a target this round.',
    linkText: 'View ROE explainer',
  },
  'new-metric-roa': {
    title: 'New Metric: ROA',
    description: 'Return on Assets is a target this round.',
    linkText: 'View ROA explainer',
  },
  'new-metric-eps': {
    title: 'New Metric: EPS',
    description: 'Earnings Per Share is a target this round.',
    linkText: 'View EPS explainer',
  },
};

// Substeps for Step 1 — Review Prior Round Results.
const STEP1_SUBSTEP_PERFORMANCE = {
  id: 'review-performance-feedback',
  title: 'Review Performance Feedback report',
  description:
    "This report explains why your team didn't hit the financial targets for the prior round.",
  linkText: 'Open performance feedback',
};

const STEP1_SUBSTEP_TEAMREPORT = {
  id: 'review-team-report',
  title: 'Review Team Report',
  description:
    "Compare your team's results against your competitors. Spot what top teams did differently so you can adjust this round.",
  linkText: 'Open team report',
};

// Builds the per-round storage key so progress is tracked
// independently per round.
const storageKey = (round) => {
  const uid = Auth.getUserId();
  return `onboarding:checklist:round${round || 'X'}:${uid || 'anon'}`;
};

// ---------- styles -------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Styled = {};

Styled.Root = styled.section`
  background: #FAFBFD;
  min-height: 100%;
  margin: 0 -40px -40px;
  padding: 64px 32px 24px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  color: #0F172A;
`;

Styled.Header = styled.div`
  max-width: 760px;
  margin: 0 auto 16px;
  text-align: center;
`;

Styled.Title = styled.h1`
  margin: 0 0 14px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.1;
  color: #0F172A;
`;

Styled.Subhead = styled.p`
  margin: 0 0 14px;
  font-size: 16px;
  color: #475569;
  line-height: 1.4;
`;

Styled.Timeline = styled.ol`
  position: relative;
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 1180px;
`;

Styled.Step = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 18px;
  padding: 10px 14px 10px 6px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: transparent;
  transition: background 200ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 25px;
    width: 2px;
    top: 0;
    bottom: -12px;
    background: #E2E8F0;
    z-index: 0;
  }

  &:first-child::before {
    top: 30px;
  }

  &:last-child::before {
    bottom: auto;
    height: 30px;
  }

  ${p => p.active && css`
    background: #DBEAFE;
  `}

  &:last-child {
    margin-bottom: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const stepCircleBase = `
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 18px;
  font-weight: 700;
  background: #FFFFFF;
  border: 2px solid #E2E8F0;
  color: #64748B;
  position: relative;
  z-index: 1;
  padding: 0;
  transition: background 200ms ease, border-color 200ms ease, color 200ms ease;

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.StepCircleStatic = styled.div`
  ${stepCircleBase}

  ${p => p.completed && css`
    background: #10B981;
    border-color: #10B981;
    color: #FFFFFF;
  `}

  ${p => (!p.completed && p.active) && css`
    background: #EFF6FF;
    border-color: #2563EB;
    color: #2563EB;
  `}
`;

Styled.StepBody = styled.div`
  padding-top: 2px;
`;

// Clickable wrapper around a parent step's title + description that
// toggles its substeps open/closed. Rendered as div+role=button so it
// can legally contain the existing <h2> and <p> children.
Styled.StepHeaderToggle = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  cursor: pointer;
  border-radius: 8px;

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }
`;

Styled.StepHeaderTextGroup = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

Styled.StepHeaderChevron = styled.span`
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: #64748B;
  font-size: 14px;
  line-height: 1;
  transition: color 150ms ease;

  ${Styled.StepHeaderToggle}:hover & {
    color: #0F172A;
  }
`;

Styled.StepTitle = styled.h2`
  margin: 0 0 4px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 800;
  font-size: 18px;
  color: #0F172A;
  transition: color 300ms ease, text-decoration-color 300ms ease;

  ${p => p.completed && css`
    color: #94A3B8;
    text-decoration: line-through;
    text-decoration-color: #CBD5E1;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.StepDescription = styled.p`
  margin: 0 0 6px;
  font-size: 16px;
  line-height: 1.45;
  color: #475569;
  max-width: 900px;
`;

Styled.SubstepGroup = styled.div`
  margin-top: 8px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 4px;
`;

Styled.SubstepRow = styled.button`
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  transition: background 150ms ease;

  & + & {
    border-top: 1px solid #E2E8F0;
  }

  &:hover {
    background: #FFFFFF;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.Checkbox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border: 2px solid #CBD5E1;
  border-radius: 5px;
  background: #FFFFFF;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  transition: background 200ms ease, border-color 200ms ease;

  ${p => p.checked && css`
    background: #10B981;
    border-color: #10B981;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.SubstepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

Styled.SubstepTitle = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #0F172A;
  transition: color 300ms ease, text-decoration-color 300ms ease;

  ${p => p.completed && css`
    color: #94A3B8;
    text-decoration: line-through;
    text-decoration-color: #CBD5E1;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

Styled.SubstepDescription = styled.span`
  font-size: 16px;
  line-height: 1.4;
  color: #475569;
`;

Styled.SubstepLink = styled.a`
  align-self: flex-start;
  font-size: 16px;
  font-weight: 700;
  color: #2563EB;
  text-decoration: none;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

Styled.CompletionBanner = styled.div`
  max-width: 760px;
  margin: 32px auto 0;
  padding: 28px 32px;
  border-radius: 20px;
  background: linear-gradient(135deg, #ECFDF5, #D1FAE5);
  border: 1px solid #A7F3D0;
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 20px;
  align-items: center;
  animation: ${fadeUp} 500ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.CompletionIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #10B981;
  color: #FFFFFF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
`;

Styled.CompletionTitle = styled.h3`
  margin: 0 0 4px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #064E3B;
`;

Styled.CompletionDescription = styled.p`
  margin: 0;
  font-size: 15px;
  color: #047857;
  line-height: 1.5;
`;

Styled.SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// ---------- modal shell -------------------------------------------

Styled.Modal = {};

Styled.Modal.Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 1000;
  animation: ${fadeIn} 200ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

Styled.Modal.Card = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 900px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  overflow: hidden;
`;

Styled.Modal.HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid #E2E8F0;
`;

Styled.Modal.Title = styled.h2`
  margin: 0;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;
`;

Styled.Modal.CloseButton = styled.button`
  background: transparent;
  border: 0;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748B;
  font-size: 22px;
  line-height: 1;

  &:hover {
    background: #F1F5F9;
    color: #0F172A;
  }
`;

// Wraps the existing What-to-Expect component inside the modal.
// The legacy components have their own gray Wrapper + 40px padding
// — we let those take over by zeroing the modal body padding.
Styled.Modal.Body = styled.div`
  overflow-y: auto;
  padding: 0;
  font-size: 16px;
  line-height: 1.55;
  color: #1F2A44;
`;

// ---------- component ---------------------------------------------

class RoundChecklist extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      completed: this.readFromStorage(props),
      // Which What-to-Expect popup is currently open. Null = none.
      activePopupId: null,
      // Gating data for substeps:
      //   - hitAllTargets: bool — gates the Performance Feedback substep
      //   - targets: { roe, roa, earnings_per_share, ... } — gates the
      //     New Metric ROE/ROA/EPS substeps
      gatingLoaded: false,
      hitAllTargets: false,
      targets: {},
      // Which parent steps are collapsed. Keyed by step id; absence
      // means expanded. Sub-sections start expanded by default so
      // users see the substeps immediately. In-memory only — resets
      // on reload.
      collapsedSteps: {},
    };
  }

  toggleStepCollapse = (stepId) => {
    this.setState((prev) => {
      const next = { ...prev.collapsedSteps };
      if (next[stepId]) delete next[stepId];
      else next[stepId] = true;
      return { collapsedSteps: next };
    });
  };

  isStepCollapsed = (stepId) => !!this.state.collapsedSteps[stepId];

  handleStepHeaderKeyDown = (stepId) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleStepCollapse(stepId);
    }
  };

  componentDidMount() {
    this.overrideParentBackgrounds();
    this.fetchGatingData();
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.handleKeydown);
    }
  }

  componentWillUnmount() {
    this.restoreParentBackgrounds();
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.handleKeydown);
    }
  }

  setRootEl = (el) => {
    this._rootEl = el;
  };

  // Esc closes any open popup.
  handleKeydown = (e) => {
    if (e.key === 'Escape' && this.state.activePopupId) {
      this.closePopup();
    }
  };

  // Match the Round1Checklist trick: parent OnboardingPage paints
  // a #f5f6f7 background; override to the softer #FAFBFD so the
  // checklist surface fills the panel.
  overrideParentBackgrounds = () => {
    if (typeof window === 'undefined' || !this._rootEl) return;
    this._modifiedNodes = [];
    let node = this._rootEl.parentElement;
    let hops = 0;
    while (node && hops < 6) {
      const cs = window.getComputedStyle(node);
      if (cs.backgroundColor === 'rgb(245, 246, 247)') {
        this._modifiedNodes.push({
          el: node,
          originalBg: node.style.background,
        });
        node.style.background = '#FAFBFD';
      }
      node = node.parentElement;
      hops += 1;
    }
  };

  restoreParentBackgrounds = () => {
    if (this._modifiedNodes) {
      this._modifiedNodes.forEach((entry) => {
        entry.el.style.background = entry.originalBg;
      });
      this._modifiedNodes = null;
    }
  };

  // ---- persistence -----------------------------------------------

  readFromStorage = (props) => {
    try {
      const round = (props || this.props).roundInUrl
        || (props || this.props).currentRound
        || 0;
      const raw = window.localStorage.getItem(storageKey(round));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return {};
      const next = {};
      parsed.forEach((id) => {
        if (typeof id === 'string') next[id] = true;
      });
      return next;
    } catch (e) {
      return {};
    }
  };

  writeToStorage = (completed) => {
    try {
      const ids = Object.keys(completed).filter(k => completed[k] === true);
      window.localStorage.setItem(storageKey(this.activeRound), JSON.stringify(ids));
    } catch (e) {
      // localStorage unavailable — silent.
    }
  };

  toggle = (id) => {
    this.setState((prev) => {
      const next = { ...prev.completed };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.writeToStorage(next);
      return { completed: next };
    });
  };

  // ---- data fetching ---------------------------------------------

  fetchGatingData = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const activeRound = this.activeRound;
    const priorRound = activeRound > 1 ? activeRound - 1 : null;

    // Current-round targets gate ROE/ROA/EPS substeps.
    const targetsReq = getRequest(
      `${apiUrl}v2/games/targets/?round_number=${activeRound}&course=${Auth.getCourseId()}`
    ).catch(() => null);

    // Prior-round teamgrading gates the Performance Feedback substep.
    // If no prior round (shouldn't happen for round 2+, but defensive),
    // assume targets were "hit" so the substep is hidden.
    const team = Teams.currentTeam();
    const teamId = team ? Number(team.id) : null;
    const gameId = Number(Auth.getGameId());
    const priorReq = (priorRound != null && teamId != null)
      ? getRequest(
          `${apiUrl}v2/games/teamgradings/?round_number=${priorRound}&game=${gameId}&team=${teamId}`
        ).catch(() => null)
      : Promise.resolve(null);

    Promise.all([targetsReq, priorReq]).then(([targetsRes, priorRes]) => {
      const targetsBody = (targetsRes && targetsRes.body) || {};
      const targets = Array.isArray(targetsBody.results)
        ? (targetsBody.results[0] || {})
        : (Array.isArray(targetsBody) ? (targetsBody[0] || {}) : targetsBody);

      // "Hit all targets" check — uses prior round's current_weighted_value.
      // If it's at 1, the team hit every target. Anything less means at
      // least one target was missed, so we show the Performance Feedback
      // substep to walk them through what went wrong.
      let hitAllTargets = false;
      if (priorRes && priorRes.body) {
        const rows = Array.isArray(priorRes.body.results) ? priorRes.body.results : [];
        const myRow = rows.find(r => Number(r.team) === teamId)
          || (rows.length > 0 ? rows[0] : null);
        if (myRow) {
          const wv = Number(myRow.current_weighted_value);
          hitAllTargets = !isNaN(wv) && wv >= 1;
        }
      }

      this.setState({
        gatingLoaded: true,
        hitAllTargets,
        targets: targets || {},
      });
    }).catch(() => {
      this.setState({ gatingLoaded: true });
    });
  };

  // ---- derived helpers -------------------------------------------

  get activeRound() {
    const { roundInUrl, currentRound } = this.props;
    return Number(roundInUrl || currentRound || 2);
  }

  get priorRound() {
    return this.activeRound > 1 ? this.activeRound - 1 : null;
  }

  // Builds the list of substep objects for Step 1 based on whether
  // the team hit every target in the prior round.
  get step1Substeps() {
    const list = [];
    if (!this.state.hitAllTargets) {
      list.push(STEP1_SUBSTEP_PERFORMANCE);
    }
    list.push(STEP1_SUBSTEP_TEAMREPORT);
    return list;
  }

  // Builds the list of "What to Expect" substep ids for Step 2,
  // gated by the round number + active targets.
  get step2SubstepIds() {
    const round = this.activeRound;
    const targets = this.state.targets || {};
    const ids = [];

    // Customer Preferences: all rounds except 1
    if (round > 1) ids.push('customer-preferences');

    // Product Strategy / Machine Options / Distribution Centers /
    // Share Buyback: round 2 only (per current spec).
    if (round === 2) {
      ids.push('product-strategy');
      ids.push('machine-options');
      ids.push('distribution-centers');
      ids.push('share-buyback');
    }

    // New Metric substeps gated on the metric being a target.
    if (Number(targets.roe) > 0) ids.push('new-metric-roe');
    if (Number(targets.roa) > 0) ids.push('new-metric-roa');
    if (Number(targets.earnings_per_share) > 0) ids.push('new-metric-eps');

    return ids;
  }

  // Total of all countable items across both steps. Drives the
  // completion banner.
  get countableIds() {
    return [
      ...this.step1Substeps.map(s => s.id),
      ...this.step2SubstepIds,
    ];
  }

  isLeafComplete = (id) => !!this.state.completed[id];

  isStep1Complete = () => {
    return this.step1Substeps.every(s => this.isLeafComplete(s.id));
  };

  isStep2Complete = () => {
    return this.step2SubstepIds.every(id => this.isLeafComplete(id));
  };

  firstIncompleteStepId = () => {
    if (!this.isStep1Complete()) return 'review-prior-results';
    if (!this.isStep2Complete()) return 'what-to-expect';
    return null;
  };

  allComplete = () => {
    const ids = this.countableIds;
    if (ids.length === 0) return false;
    return ids.every(id => this.isLeafComplete(id));
  };

  // ---- popup actions ---------------------------------------------

  openPopup = (id) => {
    this.setState({ activePopupId: id });
  };

  closePopup = () => {
    this.setState({ activePopupId: null });
  };

  // ---- URL builders ----------------------------------------------

  withRound = (path, round) => {
    const r = round != null ? round : this.activeRound;
    return `${path}${path.indexOf('?') >= 0 ? '&' : '?'}round=${r}`;
  };

  // The Performance Feedback link should point at the prior round
  // (that's the round we're reviewing).
  performanceFeedbackUrl = () => this.withRound('/performance-feedback', this.priorRound);

  // Team report likewise refers to the prior round.
  teamReportUrl = () => this.withRound('/analysis/team-reports', this.priorRound);

  // Customer Analysis lives at /ceo/analysis/market and is round-aware.
  // Uses the ACTIVE round (not prior) so students see the preferences
  // that apply to the round they're about to play.
  customerAnalysisUrl = () => this.withRound('/ceo/analysis/market', this.activeRound);

  stopRowClickPropagation = (e) => {
    if (e) e.stopPropagation();
  };

  // ---- render helpers --------------------------------------------

  renderStepCircle = (number, completed, active) => {
    const content = completed ? '✓' : number;
    // Both top-level steps are parent-only (their completion is
    // derived from substeps), so the circle is informational.
    return (
      <Styled.StepCircleStatic
        completed={completed}
        active={active}
        aria-hidden="true"
      >
        {content}
      </Styled.StepCircleStatic>
    );
  };

  // Rendered as a <button> that imperatively navigates via
  // changeLocation. Avoids nesting <a> inside the parent <button>
  // SubstepRow (invalid HTML) which was preventing reliable
  // navigation in some browsers.
  renderInternalSubstepLink = (text, href) => (
    <Styled.SubstepLink
      as="button"
      type="button"
      onClick={(e) => {
        this.stopRowClickPropagation(e);
        changeLocation(href);
      }}
    >
      {text} →
    </Styled.SubstepLink>
  );

  renderPopupSubstepLink = (text, popupId) => (
    <Styled.SubstepLink
      as="button"
      type="button"
      onClick={(e) => {
        this.stopRowClickPropagation(e);
        this.openPopup(popupId);
      }}
    >
      {text} →
    </Styled.SubstepLink>
  );

  renderSubstepRow = (id, title, description, linkNode) => {
    const isDone = this.isLeafComplete(id);
    const label = isDone
      ? `Mark ${title} as incomplete`
      : `Mark ${title} as complete`;
    return (
      <Styled.SubstepRow
        key={id}
        type="button"
        onClick={() => this.toggle(id)}
        aria-pressed={isDone}
        aria-label={label}
      >
        <Styled.Checkbox checked={isDone} aria-hidden="true">
          {isDone ? '✓' : ''}
        </Styled.Checkbox>
        <Styled.SubstepBody>
          <Styled.SubstepTitle completed={isDone}>{title}</Styled.SubstepTitle>
          {/* Link sits inline with the description so the substep
              fits on fewer rows (no extra line just for the link). */}
          <Styled.SubstepDescription>
            {description}
            {linkNode && <span>{' '}{linkNode}</span>}
          </Styled.SubstepDescription>
        </Styled.SubstepBody>
      </Styled.SubstepRow>
    );
  };

  renderStep1Substeps = () => {
    const subs = this.step1Substeps;
    if (subs.length === 0) return null;
    return (
      <Styled.SubstepGroup>
        {subs.map((sub) => {
          let linkNode = null;
          if (sub.id === 'review-performance-feedback') {
            linkNode = this.renderInternalSubstepLink(sub.linkText, this.performanceFeedbackUrl());
          } else if (sub.id === 'review-team-report') {
            linkNode = this.renderInternalSubstepLink(sub.linkText, this.teamReportUrl());
          }
          return this.renderSubstepRow(sub.id, sub.title, sub.description, linkNode);
        })}
      </Styled.SubstepGroup>
    );
  };

  renderStep2Substeps = () => {
    const ids = this.step2SubstepIds;
    if (ids.length === 0) {
      return (
        <Styled.SubstepGroup>
          <div style={{ padding: '14px 16px', fontSize: 14, color: '#475569' }}>
            No new updates to review for this round. You're ready to move on to decisions.
          </div>
        </Styled.SubstepGroup>
      );
    }
    return (
      <Styled.SubstepGroup>
        {ids.map((id) => {
          const meta = POPUP_SUBSTEPS[id];
          if (!meta) return null;
          // Customer Preferences links straight to the Customer Analysis
          // page for the active round rather than opening the modal
          // explainer, so students land on real data instead of a
          // teaser.
          const linkNode = id === 'customer-preferences'
            ? this.renderInternalSubstepLink(meta.linkText, this.customerAnalysisUrl())
            : this.renderPopupSubstepLink(meta.linkText, id);
          return this.renderSubstepRow(
            id,
            meta.title,
            meta.description,
            linkNode,
          );
        })}
      </Styled.SubstepGroup>
    );
  };

  renderPopup = () => {
    const { activePopupId } = this.state;
    if (!activePopupId) return null;
    const Component = POPUP_COMPONENTS[activePopupId];
    if (!Component) return null;
    return (
      <Styled.Modal.Backdrop onClick={this.closePopup} role="dialog" aria-modal="true">
        <Styled.Modal.Card onClick={e => e.stopPropagation()}>
          <Styled.Modal.HeaderBar>
            <Styled.Modal.Title>{POPUP_TITLES[activePopupId]}</Styled.Modal.Title>
            <Styled.Modal.CloseButton
              type="button"
              onClick={this.closePopup}
              aria-label="Close"
            >
              ×
            </Styled.Modal.CloseButton>
          </Styled.Modal.HeaderBar>
          <Styled.Modal.Body>
            {/* The legacy What-to-Expect components expect `setMode`
                and `roundInUrl` props from OnboardingPage. setMode
                is a no-op here; roundInUrl drives data lookup
                (CustomerPreferences uses it to pick the right
                round's JSON entry). */}
            <Component
              setMode={() => {}}
              roundInUrl={this.activeRound}
              currentRound={this.props.currentRound}
            />
          </Styled.Modal.Body>
        </Styled.Modal.Card>
      </Styled.Modal.Backdrop>
    );
  };

  // ---- render ----------------------------------------------------

  render() {
    if (!this.state.gatingLoaded) {
      // No skeleton needed — the checklist is short. Render
      // empty Root so parent-bg override still applies.
      return <Styled.Root innerRef={this.setRootEl} />;
    }

    const step1Complete = this.isStep1Complete();
    const step2Complete = this.isStep2Complete();
    const activeId = this.firstIncompleteStepId();
    const allDone = this.allComplete();

    return (
      <Styled.Root innerRef={this.setRootEl}>
        <Styled.Header>
          <Styled.Title>Welcome to Round {this.activeRound}!</Styled.Title>
          <Styled.Subhead>
            To get started for the round, follow the checklist below.
          </Styled.Subhead>
          <Styled.SrOnly aria-live="polite">
            {this.countableIds.filter(id => this.isLeafComplete(id)).length} of {this.countableIds.length} steps complete
          </Styled.SrOnly>
        </Styled.Header>

        <Styled.Timeline>
          {/* Step 1 — Review Prior Round Results (collapsible) */}
          <Styled.Step active={activeId === 'review-prior-results'}>
            {this.renderStepCircle(
              1,
              step1Complete,
              activeId === 'review-prior-results',
            )}
            <Styled.StepBody>
              <Styled.StepHeaderToggle
                role="button"
                tabIndex={0}
                onClick={() => this.toggleStepCollapse('review-prior-results')}
                onKeyDown={this.handleStepHeaderKeyDown('review-prior-results')}
                aria-expanded={!this.isStepCollapsed('review-prior-results')}
                aria-controls="substeps-review-prior-results"
                aria-label={this.isStepCollapsed('review-prior-results')
                  ? 'Expand Review Prior Round Results'
                  : 'Collapse Review Prior Round Results'}
              >
                <Styled.StepHeaderTextGroup>
                  <Styled.StepTitle completed={step1Complete}>
                    Review Prior Round Results
                  </Styled.StepTitle>
                  <Styled.StepDescription>
                    Reflect on what happened last round so this team can sharpen its strategy.
                  </Styled.StepDescription>
                </Styled.StepHeaderTextGroup>
                <Styled.StepHeaderChevron aria-hidden="true">
                  {this.isStepCollapsed('review-prior-results') ? '▸' : '▾'}
                </Styled.StepHeaderChevron>
              </Styled.StepHeaderToggle>
              {!this.isStepCollapsed('review-prior-results') && (
                <div id="substeps-review-prior-results">
                  {this.renderStep1Substeps()}
                </div>
              )}
            </Styled.StepBody>
          </Styled.Step>

          {/* Step 2 — What to Expect This Round (collapsible) */}
          <Styled.Step active={activeId === 'what-to-expect'}>
            {this.renderStepCircle(
              2,
              step2Complete,
              activeId === 'what-to-expect',
            )}
            <Styled.StepBody>
              <Styled.StepHeaderToggle
                role="button"
                tabIndex={0}
                onClick={() => this.toggleStepCollapse('what-to-expect')}
                onKeyDown={this.handleStepHeaderKeyDown('what-to-expect')}
                aria-expanded={!this.isStepCollapsed('what-to-expect')}
                aria-controls="substeps-what-to-expect"
                aria-label={this.isStepCollapsed('what-to-expect')
                  ? 'Expand What to Expect This Round'
                  : 'Collapse What to Expect This Round'}
              >
                <Styled.StepHeaderTextGroup>
                  <Styled.StepTitle completed={step2Complete}>
                    What to Expect This Round
                  </Styled.StepTitle>
                  <Styled.StepDescription>
                    Get up to speed on what's changing this round before the team meets to make decisions.
                  </Styled.StepDescription>
                </Styled.StepHeaderTextGroup>
                <Styled.StepHeaderChevron aria-hidden="true">
                  {this.isStepCollapsed('what-to-expect') ? '▸' : '▾'}
                </Styled.StepHeaderChevron>
              </Styled.StepHeaderToggle>
              {!this.isStepCollapsed('what-to-expect') && (
                <div id="substeps-what-to-expect">
                  {this.renderStep2Substeps()}
                </div>
              )}
            </Styled.StepBody>
          </Styled.Step>
        </Styled.Timeline>

        {allDone && (
          <Styled.CompletionBanner>
            <Styled.CompletionIcon aria-hidden="true">
              {COMPLETION_COPY.icon}
            </Styled.CompletionIcon>
            <div>
              <Styled.CompletionTitle>{COMPLETION_COPY.title}</Styled.CompletionTitle>
              <Styled.CompletionDescription>
                {COMPLETION_COPY.description}
              </Styled.CompletionDescription>
            </div>
          </Styled.CompletionBanner>
        )}

        {this.renderPopup()}
      </Styled.Root>
    );
  }
}

export default RoundChecklist;



// WEBPACK FOOTER //
// ./src/components/onboarding/welcome/RoundChecklist.js